/* ==========================================================================
   Learn Hub · assets/learn-hub.js
   Vanilla JS, no dependencies. Filters server-rendered cards by toggling the
   `hidden` attribute, so there are no network requests and no layout thrash.

   State model (AND between groups):
     source  – one blog handle or '' (all)
     topics  – set of tag handles; match "any" or "all" (section setting)
     q       – free-text search across title, excerpt, tags, source, author
   URL:  ?source=recipes&topic=sharpening,knife-care&q=gyuto
   ========================================================================== */
(() => {
  if (customElements.get('learn-hub')) return;

  const norm = (s) =>
    (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LearnHub extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      const d = this.dataset;
      this.pageSize = parseInt(d.pageSize, 10) || 12;
      this.limit = this.pageSize;
      this.matchAll = d.topicMatch === 'all';
      this.syncUrl = d.syncUrl === 'true';
      this.storeKey = `learn-hub:${location.pathname}:${this.id}`;

      this.items = Array.from(this.querySelectorAll('[data-lh-item]'), (el) => {
        el.classList.remove('lh-is-deferred'); // JS now owns visibility
        return {
          el,
          source: el.dataset.source || '',
          tags: el.dataset.tags || '|',
          text: norm(el.dataset.search),
        };
      });

      const q = (sel) => this.querySelector(sel);
      this.searchInput = q('[data-lh-search]');
      this.clearBtn = q('[data-lh-search-clear]');
      this.statusEl = q('[data-lh-status]');
      this.emptyEl = q('[data-lh-empty]');
      this.moreBtn = q('[data-lh-more]');
      this.toolbar = q('[data-lh-toolbar]');
      this.allBtn = q('.lh-toolbar [data-lh-reset]');
      this.sourceBtns = [...this.querySelectorAll('[data-lh-source]')];
      this.topicBtns = [...this.querySelectorAll('[data-lh-topic]')];

      this.state = { source: '', topics: new Set(), q: '' };
      this.readUrl();
      this.restoreSession();

      this.onClick = this.onClick.bind(this);
      this.onPageHide = () => this.saveSession();
      this.addEventListener('click', this.onClick);
      window.addEventListener('pagehide', this.onPageHide);

      if (this.searchInput) {
        let t;
        this.searchInput.addEventListener('input', () => {
          this.clearBtn && (this.clearBtn.hidden = !this.searchInput.value);
          clearTimeout(t);
          t = setTimeout(() => {
            this.state.q = this.searchInput.value;
            this.update();
          }, 120);
        });
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.searchInput.value) {
            e.preventDefault();
            this.clearSearch();
          } else if (e.key === 'Enter') {
            e.preventDefault(); // never submit/reload
          }
        });
      }

      this.render();

      if (this._restoreY != null) {
        const y = this._restoreY;
        requestAnimationFrame(() => window.scrollTo(0, y));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('pagehide', this.onPageHide);
      this._ready = false;
    }

    /* ---------- Events ---------- */
    onClick(e) {
      const el = e.target.closest('button, a');
      if (!el || !this.contains(el)) return;
      const ds = el.dataset;

      if ('lhReset' in ds) return this.reset();
      if ('lhSource' in ds) {
        this.state.source = this.state.source === ds.lhSource ? '' : ds.lhSource;
        return this.update();
      }
      if ('lhTopic' in ds) {
        const t = ds.lhTopic;
        this.state.topics.has(t) ? this.state.topics.delete(t) : this.state.topics.add(t);
        return this.update();
      }
      if ('lhMore' in ds) return this.showMore();
      if ('lhSearchClear' in ds) return this.clearSearch();

      // Category card button: filter in place (progressive enhancement of the blog link)
      if ('lhSourceLink' in ds && this.dataset.cardAction === 'filter' && this.toolbar) {
        e.preventDefault();
        this.state.source = ds.lhSourceLink;
        this.update();
        this.toolbar.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start',
        });
        const btn = this.sourceBtns.find((b) => b.dataset.lhSource === this.state.source);
        btn && btn.focus({ preventScroll: true });
      }
    }

    /* ---------- State changes ---------- */
    update() {
      this.limit = this.pageSize;
      this.classList.add('is-interactive');
      this.render();
      this.writeUrl();
    }

    reset() {
      this.state.source = '';
      this.state.topics.clear();
      this.state.q = '';
      if (this.searchInput) this.searchInput.value = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      this.update();
    }

    clearSearch() {
      this.searchInput.value = '';
      this.clearBtn && (this.clearBtn.hidden = true);
      this.state.q = '';
      this.update();
      this.searchInput.focus();
    }

    showMore() {
      const before = this.visible().length;
      this.limit += this.pageSize;
      this.classList.add('is-interactive');
      this.render();
      // Move focus to the first newly revealed card for keyboard/screen reader users
      const next = this.visible()[before];
      const link = next && next.el.querySelector('a');
      link && link.focus();
    }

    /* ---------- Matching + rendering ---------- */
    matches(item, terms) {
      const { source, topics } = this.state;
      if (source && item.source !== source) return false;
      if (topics.size) {
        const has = (t) => item.tags.includes(`|${t}|`);
        const list = [...topics];
        if (this.matchAll ? !list.every(has) : !list.some(has)) return false;
      }
      return terms.every((w) => item.text.includes(w));
    }

    render() {
      const terms = norm(this.state.q).trim().split(/\s+/).filter(Boolean);
      let total = 0;
      for (const item of this.items) {
        const ok = this.matches(item, terms);
        if (ok) total++;
        item.el.hidden = !(ok && total <= this.limit);
      }
      const shown = Math.min(total, this.limit);

      if (this.emptyEl) this.emptyEl.hidden = total > 0;
      if (this.moreBtn) this.moreBtn.hidden = total <= this.limit;

      const { source, topics } = this.state;
      if (this.allBtn) this.allBtn.setAttribute('aria-pressed', String(!source && !topics.size));
      this.sourceBtns.forEach((b) =>
        b.setAttribute('aria-pressed', String(b.dataset.lhSource === source))
      );
      this.topicBtns.forEach((b) =>
        b.setAttribute('aria-pressed', String(topics.has(b.dataset.lhTopic)))
      );

      if (this.statusEl) {
        const d = this.dataset;
        let msg;
        if (total === 0) msg = d.strNone;
        else if (shown < total) msg = d.strShowing;
        else if (total === 1) msg = d.strOne;
        else msg = d.strCount;
        msg = msg.replace('{shown}', shown).replace('{total}', total);
        if (this.statusEl.textContent.trim() !== msg) this.statusEl.textContent = msg;
      }
    }

    visible() {
      return this.items.filter((i) => !i.el.hidden);
    }

    /* ---------- URL + session persistence ---------- */
    readUrl() {
      const p = new URLSearchParams(location.search);
      const src = p.get('source');
      if (src && this.items.some((i) => i.source === src)) this.state.source = src;

      (p.get('topic') || '')
        .split(',')
        .filter(Boolean)
        .forEach((t) => {
          if (this.items.some((i) => i.tags.includes(`|${t}|`))) this.state.topics.add(t);
        });

      const q = p.get('q');
      if (q && this.searchInput) {
        this.state.q = q;
        this.searchInput.value = q;
        if (this.clearBtn) this.clearBtn.hidden = false;
      }
    }

    writeUrl() {
      if (!this.syncUrl) return;
      const url = new URL(location.href);
      const p = url.searchParams;
      const { source, topics, q } = this.state;
      source ? p.set('source', source) : p.delete('source');
      topics.size ? p.set('topic', [...topics].join(',')) : p.delete('topic');
      q.trim() ? p.set('q', q.trim()) : p.delete('q');
      const qs = p.toString();
      history.replaceState(history.state, '', url.pathname + (qs ? `?${qs}` : '') + url.hash);
    }

    saveSession() {
      try {
        sessionStorage.setItem(this.storeKey, JSON.stringify({ limit: this.limit, y: window.scrollY }));
      } catch (_) { /* storage unavailable: ignore */ }
    }

    restoreSession() {
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (!nav || nav.type !== 'back_forward') return;
        const saved = JSON.parse(sessionStorage.getItem(this.storeKey) || 'null');
        if (saved && saved.limit > this.pageSize) {
          this.limit = saved.limit;
          this._restoreY = saved.y;
        }
      } catch (_) { /* ignore */ }
    }
  }

  customElements.define('learn-hub', LearnHub);
})();